import { useEffect, useState, useCallback } from 'react';
import {
    Box, TextField, Typography, Paper, CircularProgress, Alert, Stack
} from '@mui/material';
import * as api from './deliveryPlanningTemplatesApi';
import type { DeliveryPlanningTemplate } from './deliveryPlanningTemplatesApi';
import { sx14, paperSx, FormRow, DetailHeader } from '../common/detailView';

interface Props {
    deliveryPlanningTemplateId: number | null;
}

export function DeliveryPlanningTemplateView({ deliveryPlanningTemplateId }: Props) {
    const [item, setItem] = useState<DeliveryPlanningTemplate | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [copy, setCopy] = useState<DeliveryPlanningTemplate | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            if (deliveryPlanningTemplateId === null) {
                setItem({ id: 0, deliveryPlanningTemplateName: '' });
                setIsNew(true);
                setIsEditing(true);
            } else {
                const data = await api.getDeliveryPlanningTemplate(deliveryPlanningTemplateId);
                setItem(data);
                setIsNew(false);
                setIsEditing(false);
            }
        } catch (err) {
            setError(String(err));
        } finally {
            setLoading(false);
        }
    }, [deliveryPlanningTemplateId]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    if (error && !item) return <Alert severity="error">{error}</Alert>;
    if (!item) return null;

    const startEdit = () => { setCopy(JSON.parse(JSON.stringify(item))); setIsEditing(true); };
    const cancelEdit = () => { if (copy) setItem(copy); setIsEditing(false); };

    const save = async () => {
        setSaving(true); setError(null);
        try {
            const response = isNew
                ? await api.createDeliveryPlanningTemplate(item)
                : await api.updateDeliveryPlanningTemplate(item);
            setItem(response);
            setIsNew(false);
            setIsEditing(false);
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const update = <K extends keyof DeliveryPlanningTemplate>(field: K, value: DeliveryPlanningTemplate[K]) => {
        setItem({ ...item, [field]: value });
    };

    return (
        <Paper variant="outlined" sx={paperSx}>
            <DetailHeader
                title={item.deliveryPlanningTemplateName ?? ''}
                isEditing={isEditing}
                isNew={isNew}
                saving={saving}
                canSave
                onEdit={startEdit}
                onSave={save}
                onCancel={cancelEdit}
            />

            <Box p={2}>
                <Stack spacing={2}>
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                    <FormRow label="Mall benämning">
                        {!isEditing ? (
                            <Typography sx={sx14}>{item.deliveryPlanningTemplateName}</Typography>
                        ) : (
                            <TextField size="small" fullWidth inputProps={{ maxLength: 100 }}
                                value={item.deliveryPlanningTemplateName ?? ''}
                                onChange={e => update('deliveryPlanningTemplateName', e.target.value)} />
                        )}
                    </FormRow>
                </Stack>
            </Box>

            {saving && <Box display="flex" justifyContent="center" p={1}><CircularProgress size={20} /></Box>}
        </Paper>
    );
}
